import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ItemDeAtencao, TipoDeAtencao } from '../../api/visao-geral.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { Cartao } from '../../componentes/Cartao.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeAtencao, IconeAvancar, IconeConfere } from '../../componentes/Icones.js';
import { plural } from './texto.js';
import './CartaoRequerAtencao.css';

type Props = {
  readonly itens: readonly ItemDeAtencao[];
  /**
   * A lista chegou cortada no teto de leitura do middleware. Sem isto, o
   * contador do cabeçalho apresentaria o teto (20) como se fosse o total.
   */
  readonly truncado: boolean;
  /** Instante da apuração no servidor — é dele que sai o "verificado em". */
  readonly geradoEm: string;
};

/** Categoria do item, para a linha de procedência. */
const CATEGORIA: Readonly<Record<TipoDeAtencao, string>> = {
  dispositivoSemSincronizar: 'Dispositivo',
  dispositivoBloqueado: 'Dispositivo',
  inventarioEmConflito: 'Inventário',
  divergenciaEmAberto: 'Inventário',
  substituicaoPendente: 'Substituição de SKU',
  operacaoRetida: 'Integração',
  operacaoRejeitada: 'Integração',
};

/** Filial · depósito do item, ou nada quando ele não é de um depósito. */
function localDoItem(item: ItemDeAtencao): string | null {
  return item.depositoErp === null ? null : rotuloDoLocal(item.codFil, item.depositoErp);
}

/**
 * O título da linha, montado a partir do `tipo` e dos campos.
 *
 * O servidor manda fato, não frase: a mesma disciplina de `api/erros.ts`. Assim
 * a redação muda no painel sem uma versão nova do middleware, e nenhum texto de
 * tela nasce de um `switch` escondido do outro lado da rede.
 */
export function tituloDaAtencao(item: ItemDeAtencao): string {
  // Item de depósito se escreve como filial · depósito, e não pela
  // `referencia`, que traz só o código do depósito.
  const alvo = localDoItem(item) ?? item.referencia;

  switch (item.tipo) {
    case 'dispositivoSemSincronizar':
      return `${alvo ?? 'Aparelho'} sem sincronizar`;
    case 'dispositivoBloqueado':
      return `${alvo ?? 'Aparelho'} bloqueado`;
    case 'inventarioEmConflito':
      return alvo === null ? 'Conflito de contagem' : `Conflito de contagem em ${alvo}`;
    case 'divergenciaEmAberto':
      return plural(item.quantidade, 'divergência em aberto', 'divergências em aberto');
    case 'substituicaoPendente':
      return 'Substituição de SKU aguardando aprovação';
    case 'operacaoRetida':
      return plural(item.quantidade, 'operação retida', 'operações retidas') + ' na integração';
    case 'operacaoRejeitada':
      return plural(item.quantidade, 'operação rejeitada', 'operações rejeitadas') + ' pelo ERP';
  }
}

/** A segunda linha: o que exatamente está esperando alguém. */
export function detalheDaAtencao(item: ItemDeAtencao): string {
  switch (item.tipo) {
    case 'dispositivoSemSincronizar':
      return item.desde === null
        ? 'Nunca sincronizou desde o pareamento.'
        : `Última sincronização em ${formatarDataHora(item.desde)}.`;
    case 'dispositivoBloqueado':
      return 'O aparelho não fala com o middleware até alguém desbloqueá-lo.';
    case 'inventarioEmConflito':
      return 'Contagens concorrentes do mesmo local — nenhuma é descartada sem decisão humana.';
    case 'divergenciaEmAberto':
      return item.depositoErp === null
        ? 'Diferença entre o contado e o saldo do ERP.'
        : `Diferença entre o contado e o saldo do ERP em ${rotuloDoLocal(item.codFil, item.depositoErp)}.`;
    case 'substituicaoPendente':
      return item.referencia ?? 'Item entregue diferente do solicitado.';
    case 'operacaoRetida':
      return 'Esperando decisão no painel antes de seguir para o ERP.';
    case 'operacaoRejeitada':
      return 'O ERP recusou o envio; precisa de correção e reenvio.';
  }
}

/**
 * A tela de cada tipo de operação da fila de integração.
 *
 * Os itens de integração vêm AGRUPADOS por `Operacao.tipo`, e é esse tipo que
 * chega em `referencia` — a linha tanto pode ser de coleta quanto de contagem
 * de inventário. Enquanto todas apontavam para `/movimentacoes`, o supervisor
 * clicava numa coleta retida e caía numa lista onde ela não está.
 */
const DESTINO_POR_TIPO_DE_OPERACAO: Readonly<Record<string, string>> = {
  Coleta: '/coletas',
  Movimentacao: '/movimentacoes',
  ContagemInventario: '/inventarios',
};

/**
 * Para onde a linha leva.
 *
 * Nulo quando ainda não existe tela do assunto — e aí a linha NÃO vira link:
 * um alvo de clique que não sai do lugar ensina o supervisor a não clicar em
 * nenhum dos outros. Vale também para o item que MISTURA assuntos: a rejeitada
 * é uma contagem só, de todos os tipos, então não há destino honesto.
 */
export function destinoDaAtencao(item: ItemDeAtencao): string | null {
  switch (item.tipo) {
    case 'dispositivoSemSincronizar':
    case 'dispositivoBloqueado':
      return '/dispositivos';
    case 'inventarioEmConflito':
    case 'divergenciaEmAberto':
      return item.referenciaId === null ? '/inventarios' : `/inventarios/${item.referenciaId}`;
    case 'operacaoRetida':
    case 'operacaoRejeitada':
      return item.referencia === null
        ? null
        : (DESTINO_POR_TIPO_DE_OPERACAO[item.referencia] ?? null);
    case 'substituicaoPendente':
      return null;
  }
}

/**
 * Chave de reconciliação da lista.
 *
 * Não dá para chavear por `referenciaId`: os itens da integração chegam
 * agrupados por tipo de operação, com `referenciaId` nulo em todos — três
 * linhas de `operacaoRetida` recebiam a MESMA chave, e o React passava a
 * reconciliar uma com a outra (avisando no console). O índice é aceitável
 * exatamente aqui: a lista chega inteira a cada apuração, já ordenada pelo
 * servidor, e nenhuma linha guarda estado próprio — não há foco, entrada de
 * texto nem animação para se perder quando a posição muda. O tipo e a
 * referência vão junto só para a chave se ler no inspetor.
 */
export function chaveDaAtencao(item: ItemDeAtencao, indice: number): string {
  return `${String(indice)}-${item.tipo}-${item.referenciaId ?? item.referencia ?? ''}`;
}

function ConteudoDoItem({ item }: { readonly item: ItemDeAtencao }) {
  return (
    <>
      <span className={`atencao__marca atencao__marca--${item.gravidade}`} />
      <span className="atencao__texto">
        <span className="atencao__titulo">{tituloDaAtencao(item)}</span>
        <span className="atencao__detalhe">{detalheDaAtencao(item)}</span>
        <span className="atencao__referencia mono">
          {CATEGORIA[item.tipo]}
          {item.desde !== null && ` · ${formatarDataHora(item.desde)}`}
        </span>
      </span>
    </>
  );
}

function LinhaDeAtencao({ item }: { readonly item: ItemDeAtencao }): ReactNode {
  const destino = destinoDaAtencao(item);

  if (destino === null) {
    return (
      <div className="atencao__item atencao__item--sem-destino">
        <ConteudoDoItem item={item} />
      </div>
    );
  }

  return (
    <Link className="atencao__item" to={destino}>
      <ConteudoDoItem item={item} />
      <span className="atencao__seta">
        <IconeAvancar tamanho={15} />
      </span>
    </Link>
  );
}

/**
 * Coluna "Requer atenção" da visão geral.
 *
 * Vazia, ela não some nem vira área em branco: vira confirmação verde de que
 * nada está pendente — o supervisor precisa distinguir "tudo certo" de "a tela
 * não carregou".
 */
export function CartaoRequerAtencao({ itens, truncado, geradoEm }: Props) {
  const apuracao = `Apurado pelo middleware em ${formatarDataHora(geradoEm)}.`;

  if (itens.length === 0) {
    return (
      <Cartao
        tom="sucesso"
        titulo={
          <>
            <IconeConfere tamanho={17} />
            Nada requer atenção
          </>
        }
        semRespiro
      >
        <EstadoVazio
          icone={<IconeConfere tamanho={28} />}
          titulo="Nenhum aparelho parado, nenhuma divergência e nada retido na integração."
          detalhe={apuracao}
        />
      </Cartao>
    );
  }

  return (
    <Cartao
      tom="atencao"
      titulo={
        <>
          <IconeAtencao tamanho={17} />
          Requer atenção
        </>
      }
      acessorio={
        // Lista cortada: o "+" é o que a resposta permite afirmar. O total
        // exato não existe no contrato, e escrever "20" seria dizer que são
        // vinte quando podem ser duzentos.
        <span className="atencao__contador">
          {truncado ? `${String(itens.length)}+` : itens.length}
        </span>
      }
      semRespiro
    >
      {truncado && (
        <div className="atencao__aviso">
          <AvisoDaTela tom="atencao">
            A lista atingiu o teto de leitura do middleware: há mais itens esperando alguém do que
            os {itens.length} mostrados aqui.
          </AvisoDaTela>
        </div>
      )}
      <ul className="atencao__lista">
        {itens.map((item, indice) => (
          <li key={chaveDaAtencao(item, indice)}>
            <LinhaDeAtencao item={item} />
          </li>
        ))}
      </ul>
    </Cartao>
  );
}
